import { useState, useEffect, useRef } from 'react'
import { Check, AlertTriangle, ArrowUp, ArrowDown, X, ChevronDown } from 'lucide-react'
import type { LabOrder } from '../../store/labOrdersSlice'

interface LabResultsProps {
  order: LabOrder
  onSaveResults: (data: { results: string; reference_ranges: string; abnormal_flags: string }) => Promise<void>
  onMarkReviewed: (vetId: string) => Promise<void>
  veterinarians: { id: string; name: string }[]
}

interface AbnormalFlag {
  label: string
  flag: '' | 'High' | 'Low' | 'Critical'
}

function parseAbnormalFlags(flagsStr: string): AbnormalFlag[] {
  if (!flagsStr) return []
  try {
    return JSON.parse(flagsStr) as AbnormalFlag[]
  } catch {
    return []
  }
}

export function LabResults({ order, onSaveResults, onMarkReviewed, veterinarians }: LabResultsProps) {
  const isReviewed = order.status === 'Reviewed'
  const isResultsReceived = order.status === 'Results Received'
  const canEdit = isResultsReceived

  const [results, setResults] = useState(order.results || '')
  const [referenceRanges, setReferenceRanges] = useState(order.reference_ranges || '')
  const [abnormalFlags, setAbnormalFlags] = useState<AbnormalFlag[]>(parseAbnormalFlags(order.abnormal_flags))
  const [saving, setSaving] = useState(false)
  const [newFlagLabel, setNewFlagLabel] = useState('')
  const [reviewVetId, setReviewVetId] = useState(veterinarians[0]?.id || '')
  const [vetDropdownOpen, setVetDropdownOpen] = useState(false)
  const vetDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!reviewVetId && veterinarians.length > 0) {
      setReviewVetId(veterinarians[0].id)
    }
  }, [veterinarians, reviewVetId])

  useEffect(() => {
    setResults(order.results || '')
    setReferenceRanges(order.reference_ranges || '')
    setAbnormalFlags(parseAbnormalFlags(order.abnormal_flags))
  }, [order.results, order.reference_ranges, order.abnormal_flags])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (vetDropdownRef.current && !vetDropdownRef.current.contains(e.target as Node)) {
        setVetDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSave = async () => {
    setSaving(true)
    await onSaveResults({
      results,
      reference_ranges: referenceRanges,
      abnormal_flags: JSON.stringify(abnormalFlags),
    })
    setSaving(false)
  }

  const handleAddFlag = () => {
    if (!newFlagLabel.trim()) return
    setAbnormalFlags([...abnormalFlags, { label: newFlagLabel.trim(), flag: '' }])
    setNewFlagLabel('')
  }

  const handleFlagChange = (index: number, flag: AbnormalFlag['flag']) => {
    const updated = [...abnormalFlags]
    updated[index] = { ...updated[index], flag }
    setAbnormalFlags(updated)
  }

  const handleRemoveFlag = (index: number) => {
    setAbnormalFlags(abnormalFlags.filter((_, i) => i !== index))
  }

  const handleMarkReviewed = async () => {
    if (!reviewVetId) return
    await onMarkReviewed(reviewVetId)
  }

  function flagIcon(flag: string) {
    switch (flag) {
      case 'High':
        return <ArrowUp size={12} className="text-status-warning" />
      case 'Low':
        return <ArrowDown size={12} className="text-status-info" />
      case 'Critical':
        return <AlertTriangle size={12} className="text-status-error" />
      default:
        return null
    }
  }

  function flagBadgeClass(flag: string): string {
    switch (flag) {
      case 'High':
        return 'bg-[#f0e8d0] text-status-warning'
      case 'Low':
        return 'bg-[#e0e4e8] text-status-info'
      case 'Critical':
        return 'bg-[#f0dede] text-status-error'
      default:
        return 'bg-border text-text-muted'
    }
  }

  return (
    <div data-testid="lab-results-section" className="bg-surface border border-border rounded-[6px]">
      <div className="flex items-center justify-between border-b border-border-light px-4 py-2.5">
        <h2 className="text-[14px] font-medium text-text-primary">Results</h2>
        {canEdit && (
          <button
            data-testid="save-results-button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] bg-accent text-white text-[12px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50"
          >
            <Check size={13} />
            {saving ? 'Saving...' : 'Save Results'}
          </button>
        )}
      </div>

      <div className="px-4 py-3 max-sm:px-3 flex flex-col gap-4">
        {/* Results text area */}
        <div>
          <span className="text-[11px] text-text-muted block mb-1">Lab Results</span>
          <textarea
            data-testid="results-text-area"
            value={results}
            onChange={(e) => setResults(e.target.value)}
            readOnly={!canEdit}
            rows={6}
            className={`w-full rounded-[4px] border border-border bg-base text-[13px] text-text-primary px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40 resize-y ${
              !canEdit ? 'opacity-80 cursor-default' : ''
            }`}
            placeholder="Enter lab results..."
          />
        </div>

        {/* Reference ranges */}
        <div>
          <span className="text-[11px] text-text-muted block mb-1">Reference Ranges</span>
          <textarea
            data-testid="reference-ranges-area"
            value={referenceRanges}
            onChange={(e) => setReferenceRanges(e.target.value)}
            readOnly={!canEdit}
            rows={4}
            className={`w-full rounded-[4px] border border-border bg-base text-[13px] text-text-primary px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40 resize-y ${
              !canEdit ? 'opacity-80 cursor-default' : ''
            }`}
            placeholder="Enter reference ranges (e.g., WBC: 5.5–16.9 x10^3/uL)..."
          />
        </div>

        {/* Abnormal flags */}
        <div>
          <span className="text-[11px] text-text-muted block mb-1">Abnormal Flags</span>
          {abnormalFlags.length > 0 && (
            <div className="flex flex-col gap-1.5 mb-2" data-testid="abnormal-flags-list">
              {abnormalFlags.map((item, i) => (
                <div
                  key={i}
                  data-testid={`abnormal-flag-${i}`}
                  className="flex items-center gap-2 text-[13px]"
                >
                  <span className="min-w-[100px] text-text-primary">{item.label}</span>
                  {item.flag && (
                    <span
                      data-testid={`flag-badge-${i}`}
                      className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${flagBadgeClass(item.flag)}`}
                    >
                      {flagIcon(item.flag)}
                      {item.flag}
                    </span>
                  )}
                  {canEdit && (
                    <div className="flex items-center gap-1 ml-auto flex-wrap">
                      {(['', 'High', 'Low', 'Critical'] as const).map((f) => (
                        <button
                          key={f}
                          type="button"
                          data-testid={`flag-option-${i}-${f || 'normal'}`}
                          onClick={() => handleFlagChange(i, f)}
                          className={`h-5 px-1.5 rounded-[3px] text-[10px] cursor-pointer transition-colors duration-100 ${
                            item.flag === f
                              ? 'bg-accent text-white'
                              : 'bg-border text-text-muted hover:bg-hover'
                          }`}
                        >
                          {f || 'Normal'}
                        </button>
                      ))}
                      <button
                        type="button"
                        data-testid={`flag-remove-${i}`}
                        onClick={() => handleRemoveFlag(i)}
                        className="ml-1 text-text-muted hover:text-status-error cursor-pointer"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <div className="flex items-center gap-2">
              <input
                data-testid="new-flag-label"
                type="text"
                value={newFlagLabel}
                onChange={(e) => setNewFlagLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddFlag()}
                placeholder="Value name (e.g., WBC)"
                className="h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
              />
              <button
                type="button"
                data-testid="add-flag-button"
                onClick={handleAddFlag}
                className="h-7 px-2 rounded-[4px] border border-border text-[12px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
              >
                Add Flag
              </button>
            </div>
          )}
        </div>

        {/* Mark as Reviewed */}
        {isResultsReceived && (
          <div className="flex items-center gap-3 pt-2 border-t border-border-light">
            <div className="relative" ref={vetDropdownRef} data-testid="review-vet-select">
              <button
                type="button"
                data-testid="review-vet-select-trigger"
                onClick={() => setVetDropdownOpen(!vetDropdownOpen)}
                className="inline-flex items-center justify-between gap-1 h-7 min-w-[140px] px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary cursor-pointer hover:bg-hover transition-colors duration-100"
              >
                <span>{veterinarians.find(v => v.id === reviewVetId)?.name || 'Select vet...'}</span>
                <ChevronDown size={12} className="text-text-muted" />
              </button>
              {vetDropdownOpen && (
                <div
                  data-testid="review-vet-select-options"
                  className="absolute top-full left-0 mt-1 min-w-[140px] rounded-[4px] border border-border bg-surface shadow-md z-50"
                >
                  {veterinarians.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`review-vet-option-${v.id}`}
                      onClick={() => { setReviewVetId(v.id); setVetDropdownOpen(false) }}
                      className={`block w-full text-left px-2 py-1.5 text-[12px] cursor-pointer transition-colors duration-100 ${
                        v.id === reviewVetId ? 'bg-accent/10 text-accent font-medium' : 'text-text-primary hover:bg-hover'
                      }`}
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              data-testid="mark-reviewed-button"
              onClick={handleMarkReviewed}
              className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[4px] bg-accent text-white text-[12px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
            >
              <Check size={13} />
              Mark as Reviewed
            </button>
          </div>
        )}

        {/* Reviewed info */}
        {isReviewed && order.reviewed_by_name && (
          <div className="flex items-center gap-4 pt-2 border-t border-border-light text-[12px] text-text-muted" data-testid="reviewed-info">
            <span data-testid="reviewed-by">Reviewed by: {order.reviewed_by_name}</span>
            {order.reviewed_at && (
              <span data-testid="reviewed-date">
                Review date: {new Date(order.reviewed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
